import React from 'react'
import { API_PATH_PORTAL_BOOTSTRAP } from '../../../../config'
import { getApiConfig, getLocalServerURL } from '../../../../utils/getApiConfig'
import {
  Profile,
  ProfileValues,
  Subscription,
  Transaction,
} from '../../../Profile/types'
import { UnitMaster } from '../../../Units/reducer'

interface AdminUnitDetails {
  profile: Profile
  transactions: Transaction[]
  subscriptions: Subscription[]
}

interface UseAdminUnitDetailsResult extends AdminUnitDetails {
  loading: boolean
  error: string | null
}

const EMPTY_ADMIN_UNIT_DETAILS: AdminUnitDetails = {
  profile: ProfileValues,
  transactions: [],
  subscriptions: [],
}

export const useAdminUnitDetails = (
  selectedRow: UnitMaster | null
): UseAdminUnitDetailsResult => {
  const [data, setData] = React.useState<AdminUnitDetails>(
    EMPTY_ADMIN_UNIT_DETAILS
  )
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let isActive = true

    const customerProfileId = Number(selectedRow?.customerProfileId)
    if (
      !selectedRow?.customerProfileId ||
      !Number.isFinite(customerProfileId)
    ) {
      setData(EMPTY_ADMIN_UNIT_DETAILS)
      setLoading(false)
      setError(null)
      return
    }

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const config = await getApiConfig({
          body: {
            customerProfileId,
          },
        })

        if (config == null) {
          throw new Error('Could not authorize admin request')
        }

        const response = await fetch(
          getLocalServerURL(API_PATH_PORTAL_BOOTSTRAP),
          config
        )

        if (!response.ok) {
          throw new Error('Failed to load unit details')
        }

        const nextData = (await response.json()) as Partial<AdminUnitDetails>

        if (!isActive) {
          return
        }

        setData({
          profile: nextData.profile ?? ProfileValues,
          transactions: nextData.transactions ?? [],
          subscriptions: nextData.subscriptions ?? [],
        })
      } catch (loadError) {
        if (!isActive) {
          return
        }

        setData(EMPTY_ADMIN_UNIT_DETAILS)
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load unit details'
        )
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [selectedRow?.customerProfileId])

  return {
    ...data,
    loading,
    error,
  }
}
